import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getUser, getNavigationItems, hasPageAccess, getRoleDisplayName, isAdmin, isSuperAdmin } from '../../utils/auth';

/**
 * Demo component to test role-based access control
 * Shows what pages the current user can access based on their role
 */
const RoleAccessDemo = () => {
  const navigate = useNavigate();
  const user = getUser();
  const navigationItems = getNavigationItems();

  if (!user) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-md">
        <p className="text-red-800">Not authenticated</p>
      </div>
    );
  }

  const testPages = [
    { path: '/dashboard', name: 'Student Dashboard' },
    { path: '/superadmin-dashboard', name: 'Superadmin Dashboard' },
    { path: '/partner-dashboard', name: 'Partner Dashboard' },
    { path: '/admin/ai-config', name: 'AI Configuration' },
    { path: '/admin/tenants', name: 'Manage Tenants' },
    { path: '/admin/audit-logs', name: 'Audit Logs' },
    { path: '/admin/data-ingestion', name: 'Data Ingestion' },
    { path: '/analytics', name: 'Analytics' },
    { path: '/settings/tenant', name: 'Tenant Settings' },
    { path: '/ai/services', name: 'AI Services' },
    { path: '/universities', name: 'Universities' },
    { path: '/programs', name: 'Programs' }
  ];

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Role-Based Access Control Demo</h2>
        
        {/* Current User Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
          <h3 className="text-lg font-semibold text-blue-900">Current User</h3>
          <div className="mt-2 space-y-1">
            <p><span className="font-medium">Name:</span> {user.name}</p>
            <p><span className="font-medium">Email:</span> {user.email}</p>
            <p><span className="font-medium">Role:</span> {getRoleDisplayName(user.role)}</p>
            <p><span className="font-medium">Is Admin:</span> {isAdmin() ? '✅ Yes' : '❌ No'}</p>
            <p><span className="font-medium">Is Super Admin:</span> {isSuperAdmin() ? '✅ Yes' : '❌ No'}</p>
          </div>
        </div>

        {/* Navigation Items */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Available Navigation Items</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {navigationItems.map((item, index) => (
              <button
                key={index}
                onClick={() => navigate(item.path)}
                className="p-3 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 text-left"
              >
                <div className="font-medium text-green-900">{item.label}</div>
                <div className="text-sm text-green-700">{item.path}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Page Access Test */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Page Access Test</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {testPages.map((page, index) => {
              const hasAccess = hasPageAccess(page.path);
              return (
                <div
                  key={index}
                  className={`p-3 border rounded-md ${
                    hasAccess 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-medium">{page.name}</div>
                      <div className="text-sm text-gray-600">{page.path}</div>
                    </div>
                    <div className={`text-lg ${hasAccess ? 'text-green-600' : 'text-red-600'}`}>
                      {hasAccess ? '✅' : '❌'}
                    </div>
                  </div>
                  {hasAccess && (
                    <button
                      onClick={() => navigate(page.path)}
                      className="mt-2 text-sm text-blue-600 hover:text-blue-800"
                    >
                      → Navigate
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-6 bg-gray-50 border border-gray-200 rounded-md p-4">
          <h4 className="font-semibold text-gray-900">Test Instructions:</h4>
          <ul className="mt-2 text-sm text-gray-700 space-y-1">
            <li>• Green items = You have access</li>
            <li>• Red items = Access denied</li>
            <li>• Try navigating to restricted pages - you'll be redirected</li>
            <li>• Switch users to test different role permissions</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default RoleAccessDemo;